/* Verificação deliberadamente frouxa: a única prova de que um e-mail existe é a mensagem
   chegar nele. O que esta função evita é o erro de digitação óbvio — texto sem arroba, sem
   ponto no domínio, com espaço no meio — que faria a pessoa culpar a senha por um engano
   no e-mail. Regra estrita aqui só cria falso negativo em endereço válido e incomum. */
const FORMA_DE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emailParecePlausivel(email: string): boolean {
  return FORMA_DE_EMAIL.test(email.trim());
}

/* ---------------------------------------------------------------------------------- CNPJ */

/* O CNPJ é a chave canônica da empresa no portal (ADR 0001), então conferir o dígito
   verificador aqui não é preciosismo: um dígito trocado cria uma SEGUNDA organização para o
   mesmo cliente, com esteira, usuários e histórico próprios — e desfazer isso depois é caro.
   O servidor confere de novo; esta checagem existe para o erro aparecer no campo, ao lado de
   quem digitou, e não numa mensagem de erro depois do envio. */

const PESOS_DO_PRIMEIRO_DIGITO = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;
const PESOS_DO_SEGUNDO_DIGITO = [6, ...PESOS_DO_PRIMEIRO_DIGITO] as const;

export function apenasDigitos(valor: string): string {
  return valor.replace(/\D/g, "");
}

function digitoVerificador(base: string, pesos: readonly number[]): string {
  const soma = pesos.reduce((total, peso, indice) => total + Number(base[indice]) * peso, 0);
  const resto = soma % 11;
  return String(resto < 2 ? 0 : 11 - resto);
}

export function cnpjValido(valor: string): boolean {
  const digitos = apenasDigitos(valor);
  if (digitos.length !== 14) return false;
  // Repetição do mesmo dígito passa na conta do verificador e não é CNPJ de ninguém.
  if (/^(\d)\1{13}$/.test(digitos)) return false;

  const corpo = digitos.slice(0, 12);
  const primeiro = digitoVerificador(corpo, PESOS_DO_PRIMEIRO_DIGITO);
  const segundo = digitoVerificador(corpo + primeiro, PESOS_DO_SEGUNDO_DIGITO);
  return digitos.slice(12) === primeiro + segundo;
}

/** Máscara de digitação: `11222333000144` vira `11.222.333/0001-44` conforme se digita. */
export function mascararCnpj(valor: string): string {
  const digitos = apenasDigitos(valor).slice(0, 14);
  const partes = [
    digitos.slice(0, 2),
    digitos.slice(2, 5),
    digitos.slice(5, 8),
    digitos.slice(8, 12),
    digitos.slice(12, 14),
  ].filter((parte) => parte.length > 0);

  let texto = partes[0] ?? "";
  if (partes[1]) texto += `.${partes[1]}`;
  if (partes[2]) texto += `.${partes[2]}`;
  if (partes[3]) texto += `/${partes[3]}`;
  if (partes[4]) texto += `-${partes[4]}`;
  return texto;
}

/** Máscara de telefone brasileiro conforme se digita: `47984566360` vira `(47) 98456-6360` e
 *  `4734567890` vira `(47) 3456-7890`. Colado com o DDI (`+55 47 98456-6360`), o `55` sai. */
export function mascararTelefone(valor: string): string {
  let digitos = apenasDigitos(valor);
  if (digitos.length > 11 && digitos.startsWith("55")) digitos = digitos.slice(2);
  digitos = digitos.slice(0, 11);
  if (digitos.length === 0) return "";
  if (digitos.length <= 2) return `(${digitos}`;
  const ddd = digitos.slice(0, 2);
  const numero = digitos.slice(2);
  // Com 9 dígitos depois do DDD é celular (5+4); até 8, fixo (4+4).
  const corte = numero.length > 8 ? 5 : 4;
  const fim = numero.slice(corte);
  return `(${ddd}) ${numero.slice(0, corte)}${fim ? `-${fim}` : ""}`;
}

/** Telefone completo: DDD + 8 dígitos (fixo) ou DDD + 9 (celular). */
export function telefoneCompleto(valor: string): boolean {
  const digitos = apenasDigitos(valor);
  return digitos.length === 10 || digitos.length === 11;
}
